import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { useAuthStore } from "../stores/auth";
import { validateAttachment, canPreviewInBrowser, formatMaxSize } from "../lib/fileSafety";

const BUCKET = "entity-attachments";

interface AttachmentRow {
  id: number;
  entity_type: string;
  entity_id: string;
  storage_path: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_by: string | null;
  uploaded_at: string;
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentKeys(entityType: string, entityId: string) {
  return ["attachments", entityType, entityId] as const;
}

function useAttachments(entityType: string, entityId: string) {
  return useQuery({
    queryKey: attachmentKeys(entityType, entityId),
    queryFn: async (): Promise<AttachmentRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .schema("platform")
        .from("entity_attachments")
        .select("*")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as AttachmentRow[];
    },
    enabled: Boolean(entityType && entityId),
  });
}

export interface EntityFilesListProps {
  entityType: "person" | "organization" | "deal" | "activity";
  entityId: string;
}

export function EntityFilesList({ entityType, entityId }: EntityFilesListProps) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const { data: attachments = [], isLoading } = useAttachments(entityType, entityId);

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: storagePath, error } = await (supabase.rpc as any)("manna_delete_attachment", {
        p_id: id,
      });
      if (error) throw error;
      // Best-effort storage deletion — don't block on failure.
      await supabase.storage
        .from(BUCKET)
        .remove([storagePath as string])
        .catch(() => {});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: attachmentKeys(entityType, entityId) }),
  });

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploadError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const validationError = validateAttachment(file);
        if (validationError) {
          throw new Error(validationError);
        }
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "bin";
        const storagePath = `${entityType}/${entityId}/${crypto.randomUUID()}.${ext}`;

        const { error: uploadErr } = await supabase.storage
          .from(BUCKET)
          .upload(storagePath, file, { upsert: false, contentType: file.type });
        if (uploadErr) throw uploadErr;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: regErr } = await (supabase.rpc as any)("manna_register_attachment", {
          payload: {
            entity_type: entityType,
            entity_id: entityId,
            storage_path: storagePath,
            filename: file.name,
            mime_type: file.type || null,
            size_bytes: file.size,
          },
        });
        if (regErr) throw regErr;
      }
      qc.invalidateQueries({ queryKey: attachmentKeys(entityType, entityId) });
    } catch (e: unknown) {
      setUploadError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDownload = async (row: AttachmentRow) => {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(row.storage_path, 60);
    if (error || !data) return;
    const a = document.createElement("a");
    a.href = data.signedUrl;
    a.download = row.filename;
    a.click();
  };

  // Default click action: preview in a new tab when the browser can render the
  // type, otherwise fall back to a download (item 3, 2026-06-05).
  const handleOpen = async (row: AttachmentRow) => {
    if (!canPreviewInBrowser(row.mime_type, row.filename)) {
      await handleDownload(row);
      return;
    }
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(row.storage_path, 60);
    if (error || !data) return;
    window.open(data.signedUrl, "_blank", "noopener");
  };

  return (
    <Box>
      <input
        ref={fileRef}
        type="file"
        multiple
        hidden
        onChange={(e) => void handleFiles(e.target.files)}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {attachments.length} file{attachments.length !== 1 ? "s" : ""}
        </Typography>
        <Button
          size="small"
          startIcon={uploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          Upload
        </Button>
      </Stack>

      {uploadError && (
        <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mb: 1 }}>
          {uploadError}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={20} />
        </Box>
      ) : attachments.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            py: 3,
            textAlign: "center",
            cursor: uploading ? "default" : "pointer",
            "&:hover": { borderColor: uploading ? "divider" : "primary.main" },
          }}
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void handleFiles(e.dataTransfer.files);
          }}
        >
          <CloudUploadIcon color="action" sx={{ mb: 0.5 }} />
          <Typography variant="caption" display="block" color="text.secondary">
            Drop files here or click Upload
          </Typography>
          <Typography variant="caption" display="block" color="text.disabled">
            Up to {formatMaxSize()}. Executable and script files are blocked.
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {attachments.map((row) => (
            <ListItem
              key={row.id}
              disableGutters
              secondaryAction={
                <Stack direction="row">
                  {canPreviewInBrowser(row.mime_type, row.filename) && (
                    <Tooltip title="Open preview in new tab">
                      <IconButton size="small" onClick={() => void handleOpen(row)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Download">
                    <IconButton size="small" onClick={() => void handleDownload(row)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {row.uploaded_by === user?.id && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              }
              sx={{ pr: 12 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <AttachFileIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary={row.filename}
                secondary={formatBytes(row.size_bytes)}
                onClick={() => void handleOpen(row)}
                primaryTypographyProps={{
                  variant: "body2",
                  noWrap: true,
                  sx: {
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  },
                }}
                secondaryTypographyProps={{ variant: "caption" }}
                sx={{ cursor: "pointer" }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
